/**
 * Feature E02-F02 — editable Project DETAIL header (T03 + T05 + T06).
 *
 * The host that turns the read-only 3-column header into an INLINE-EDIT card with
 * staged updates and a deferred Save / Cancel (legacy `../EER app/public/assets/
 * js/project.js` → renderProjectHeader / saveProjectChanges / saveContactEdit and
 * the `state.unsavedUpdates` bag). It composes the already-built, unit-tested
 * atoms — it owns NO business rule of its own; every decision comes from
 * `projectDetailModel.ts` and `projectUnsavedModel.ts`:
 *
 *   - T03 — role name is an editable text input; the status badge paints green
 *     when `status === 'active'` (statusBadge); an empty role name BLOCKS save
 *     (isRoleNameValid → EMPTY_ROLE_NAME_ALERT); Save persists via PATCH
 *     /projects/:id (useUpdateProject) and Cancel reverts every staged field.
 *   - T05 — the client field is the `ClientAutocomplete` over the `GET /clients`
 *     cache (useClientsCache); selecting/typing stages `{client_name, client_id}`
 *     (resolveClientName — incl. the rename→client_id:null edge).
 *   - T06 — the contact is edited as `{first_name, last_name, email}`
 *     (splitContactName / assembleContactName); on save the api upserts/links the
 *     contact (services/projectMutations.ts CONTACT logic) and the re-read card
 *     shows the new contact_name / contact_email.
 *
 * Staging model: every edit mutates a local `unsavedUpdates` bag (the legacy
 * `state.unsavedUpdates`); Save collects it via `collectPendingUpdates` and fires
 * ONE PATCH; Cancel resets the bag to the project's current values. The "dirty"
 * flag (Save enabled / Cancel shown) comes from `hasUnsavedChanges` over the same
 * snapshot, so the button state can never drift from the actual staged edits.
 *
 * Thin shell — wiring only; the rules live in the two model modules so they stay
 * DOM-free and unit-tested.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type JSX } from 'react';
import { contactEmailError, type DetailProject } from '@eer/shared';
import { errorMessage } from '../api/client.js';
import { useToast } from '../ui/ToastContext.js';
import { useRegisterUnsavedSource, useUnsavedGuard } from '../ui/UnsavedGuard.js';
import { ClientAutocomplete } from './ClientAutocomplete.js';
import { useClientsCache, useUpdateProject } from './projectDetailApi.js';
import { forgetFreshDraft } from './freshDraftRegistry.js';
import {
  CONTACT_EMAIL_FIELD_KEY,
  EMPTY_ROLE_NAME_ALERT,
  MISSING_FIELD_HIGHLIGHT_CLASS,
  headerFields,
  isRoleNameValid,
  shouldHighlightMissing,
  splitContactName,
  type ClientResolution,
} from './projectDetailModel.js';
import {
  collectPendingUpdates,
  hasUnsavedChanges,
  resetUnsavedUpdates,
  type UnsavedUpdates,
} from './projectUnsavedModel.js';
import { routeToPath, routeToState } from '../router/routes.js';

/**
 * Navigate to the Inbox from the project header WITHOUT threading a `navigate`
 * prop all the way down from `AppShell` (the shell mounts `ProjectDetailView`
 * with only a `projectId`). Mirrors `InboxView`'s own `openProjectViaHistory`
 * (same reasoning, opposite direction): push the same history entry `useRouter`
 * expects, then dispatch `popstate` so it restores the Inbox view.
 *
 * Carries the source email's id in the route state (`routeToState`/`stateToRoute`
 * round-trip `id` for any view, not just `project`) so `AppShell` can pass it to
 * `InboxView` and pre-select that specific email — landing on the generic Inbox
 * list with nothing opened does not satisfy "переходит к письму" (E04-F02-T08).
 */
function openInboxViaHistory(sourceEmailId: number): void {
  if (typeof window === 'undefined') return;
  const route = { view: 'inbox' as const, id: String(sourceEmailId) };
  window.history.pushState(routeToState(route), '', routeToPath(route));
  window.dispatchEvent(new PopStateEvent('popstate', { state: routeToState(route) }));
}

export interface ProjectDetailHeaderProps {
  /** The project core projection (from the detail card). */
  readonly project: DetailProject;
  /**
   * PK-QA-1 UX — form-field keys the Pre-Kickoff analysis 400 `VALIDATION_ERROR`
   * flagged as missing (`client_name` / `role_name` / `contact_email`). Any field
   * whose key is in this set AND is still blank gets a thin red outline so the user
   * sees WHERE to add the detail (the panel's toast only names them). Optional — the
   * header renders identically when it is absent.
   */
  readonly missingFields?: ReadonlySet<string>;
  /**
   * Clear the whole missing-fields set (owned by the shared parent). Called on a
   * successful header Save so a stale highlight never lingers after the user has
   * saved their edits.
   */
  readonly onClearMissingFields?: () => void;
  /**
   * E12-F17-T15 — set while the card is a fresh, never-Saved "New Project" draft.
   * Cancel is then always enabled and, after resetting the form, abandons the
   * create (the parent discards the empty draft and returns to the list).
   */
  readonly onDiscardDraft?: () => void;
}

/** Local form snapshot mirrored into the `unsavedUpdates` bag on every keystroke. */
interface HeaderForm {
  readonly roleName: string;
  readonly clientName: string;
  /** `undefined` = untouched, `null` = create-new, number = linked client id. */
  readonly clientId: number | null | undefined;
  readonly contactFirst: string;
  readonly contactLast: string;
  readonly contactEmail: string;
}

/**
 * E02-F07-T08 — the role is edited in a wrapping textarea, but it is still a
 * one-line value: collapse any pasted line breaks into single spaces.
 */
function toSingleLine(value: string): string {
  return value.replace(/[ \t]*\r?\n[ \t]*/g, ' ');
}

/** Derive the initial editable form values from the project. */
function initialForm(project: DetailProject): HeaderForm {
  const split = splitContactName(project.contact_name);
  return {
    roleName: (project.role_name ?? '').trim(),
    clientName: (project.client_name ?? '').trim(),
    clientId: undefined,
    contactFirst: split.first_name,
    contactLast: split.last_name,
    contactEmail: (project.contact_email ?? '').trim(),
  };
}

/**
 * Per-field diff of the editable form against a base snapshot → the staged
 * `unsavedUpdates` bag. Single source of truth for "which fields differ": reused
 * by BOTH the dirty `unsaved` memo (form vs the CURRENT base) and the external-
 * resync gate (form vs the PREVIOUS base snapshot, PK-QA-10) so the two can never
 * disagree on what counts as an edit.
 */
function diffForm(form: HeaderForm, base: HeaderForm): UnsavedUpdates {
  const bag = resetUnsavedUpdates();
  if (form.roleName !== base.roleName) bag.role_name = form.roleName;
  // PK-QA-12 — trim at the SAVE boundary: `form.clientName` now carries the user's
  // RAW typed text (spaces preserved live so multi-word names can be typed), so an
  // untrimmed name must never reach the PATCH. `base.clientName` is already trimmed
  // (`initialForm` trims when reading the saved project), so comparing the trimmed
  // form value against it keeps the exact pre-PK-QA-12 dirtiness semantics — back
  // when `resolveClientName` trimmed eagerly, `form.clientName` was already trimmed.
  const clientName = form.clientName.trim();
  if (clientName !== base.clientName) bag.client_name = clientName;
  if (form.clientId !== undefined) bag.client_id = form.clientId;
  const contactChanged =
    form.contactFirst !== base.contactFirst ||
    form.contactLast !== base.contactLast ||
    form.contactEmail !== base.contactEmail;
  if (contactChanged) {
    bag.contact = {
      first_name: form.contactFirst,
      last_name: form.contactLast,
      email: form.contactEmail,
    };
  }
  return bag;
}

export function ProjectDetailHeader({
  project,
  missingFields,
  onClearMissingFields,
  onDiscardDraft,
}: ProjectDetailHeaderProps): JSX.Element {
  const toast = useToast();
  const clientsQuery = useClientsCache();
  const updateMutation = useUpdateProject();
  // The navigation gate (E02-F03-T08) — used by the "source email" jump below,
  // which pushes history directly instead of going through the shell's navigate.
  const { guard } = useUnsavedGuard();

  // The editable form. The `useState` initializer runs ONLY on mount, so it seeds
  // `form` once — ProjectDetailView's per-id `key` guarantees a fresh mount when
  // the project IDENTITY changes. A re-read of the SAME project (an external
  // refetch — e.g. Apply-to-Project invalidating the shared detail cache) does NOT
  // re-run the initializer; the resync effect below adopts those fresh values into
  // `form` when the header has no in-progress edit of its own (PK-QA-10).
  const [form, setForm] = useState<HeaderForm>(() => initialForm(project));

  // Latest `form`, readable from the resync effect WITHOUT making `form` one of its
  // dependencies — the resync must fire on external `project` changes only, never on
  // the user's own keystrokes. Writing a ref during render is the sanctioned
  // "cache the current value for later" pattern.
  const formRef = useRef(form);
  formRef.current = form;

  // FP-09 — the contact block is READ-ONLY by default (name + email as text) with
  // a pencil that reveals the {first,last,email} inputs, mirroring the v1 navy
  // banner. Client + Role stay inline-editable (the dashboard/detail e2e type
  // straight into `project-detail-client-autocomplete-input` / `-role-input`).
  const [editingContact, setEditingContact] = useState(false);

  // PK-QA-1 UX — when the Pre-Kickoff analysis flags the contact email as missing,
  // its input lives behind the read-only pencil toggle. Auto-open contact edit so
  // the highlighted field is actually visible (a highlighted-but-hidden input is
  // useless). Keyed ONLY off `missingFields` (a new Set per validation failure) so
  // it never fights a manual close — toggling the pencil does not re-trigger it.
  useEffect(() => {
    if (missingFields?.has(CONTACT_EMAIL_FIELD_KEY)) {
      setEditingContact(true);
    }
  }, [missingFields]);

  const base = useMemo(() => initialForm(project), [project]);
  const header = headerFields(project);

  // PK-QA-10 — the base snapshot `form` was last in sync with. An EXTERNAL refetch
  // (Apply-to-Project, and any neighbour mutation that invalidates the shared
  // detail cache) delivers a fresh `project` with the SAME id and no remount →
  // `base` advances to the new values while `form` stays stale. That both hides the
  // new values in the inputs and makes Save falsely read as dirty (worst: clicking
  // that spurious Save would PATCH the stale `form` and silently revert the
  // just-applied change). Adopt the fresh values into `form` — but ONLY when the
  // header has no in-progress edit of its own. The gate compares `form` against the
  // PREVIOUS base snapshot (this ref), NOT the current `dirty` (form vs the NEW
  // base): after an external refetch `dirty` is already true in exactly the case we
  // must resync, so it cannot be the gate.
  const prevBaseRef = useRef(base);
  useEffect(() => {
    // `form == prevBase` → the header had no local edit relative to what it was
    // showing → the change is purely external → take the fresh project values.
    // `form != prevBase` → the user is mid-edit → leave `form` untouched (it keeps
    // reading dirty against the NEW base until they finish/discard — which is
    // correct; we never merge or clobber an in-progress edit). Same per-field diff +
    // dirtiness predicate as the `unsaved`/`dirty` path below, only against the
    // previous base instead of the current one.
    const localEdits = diffForm(formRef.current, prevBaseRef.current);
    const headerHadNoEdit = !hasUnsavedChanges({
      unsavedUpdates: localEdits,
      hasCurrentProject: true,
    });
    if (headerHadNoEdit) {
      setForm(initialForm(project));
    }
    prevBaseRef.current = base;
  }, [project, base]);

  // Build the unsaved bag from the diff between the form and the project's
  // current values — only changed fields are staged (legacy state.unsavedUpdates).
  const unsaved = useMemo<UnsavedUpdates>(() => diffForm(form, base), [form, base]);

  const dirty = hasUnsavedChanges({ unsavedUpdates: unsaved, hasCurrentProject: true });
  const roleValid = isRoleNameValid(form.roleName);
  const saving = updateMutation.isPending;

  // E02-F03-T10 — a malformed contact email is caught HERE, before the PATCH (the
  // api rejects it with 400 anyway — Ксения 12.10: `ksenia.bussen#gmail.com` gave
  // only reasonless «Could not save the project» toasts). Checked only while the
  // contact is actually being changed: an untouched contact is never sent, so a
  // legacy stored value must not block unrelated edits. Same shared rule as the api.
  const contactEmailIssue = unsaved.contact ? contactEmailError(form.contactEmail) : null;

  // PK-QA-1 UX — per-field "missing required" outline. `shouldHighlightMissing`
  // self-clears the instant the field is non-blank, so typing removes the outline
  // with no extra handler (the keystroke already re-renders via setForm).
  const highlightClient = shouldHighlightMissing('client_name', form.clientName, missingFields);
  const highlightRole = shouldHighlightMissing('role_name', form.roleName, missingFields);
  const highlightContactEmail = shouldHighlightMissing(
    CONTACT_EMAIL_FIELD_KEY,
    form.contactEmail,
    missingFields,
  );

  /** Stage a client autocomplete resolution (name + id, incl. rename→null). */
  function onClientResolve(res: ClientResolution): void {
    setForm((f) => ({ ...f, clientName: res.client_name, clientId: res.client_id }));
  }

  /** Reset every staged edit back to the project's current values (Cancel). */
  function onCancel(): void {
    setForm(initialForm(project));
    setEditingContact(false);
  }

  /** Cancel button: reset; on a fresh draft also abandon the create (E12-F17-T15). */
  function onCancelClick(): void {
    onCancel();
    onDiscardDraft?.();
  }

  /** Read-only contact display (name + email) shown before the pencil is used. */
  const contactName = `${form.contactFirst} ${form.contactLast}`.trim();

  /**
   * Collect the staged edits and fire one PATCH. Awaitable (E02-F03-T08): the
   * unsaved-changes dialog's "Save and exit" defers the pending navigation until
   * this resolves, and a REJECTION must keep the dialog open (the user's edits
   * are still unsaved), so every failure path throws instead of swallowing.
   */
  async function saveStagedEdits(): Promise<void> {
    if (!roleValid) {
      toast.error(EMPTY_ROLE_NAME_ALERT);
      throw new Error(EMPTY_ROLE_NAME_ALERT);
    }
    if (contactEmailIssue) {
      // Reveal the (possibly collapsed) contact editor so the outlined field is visible.
      setEditingContact(true);
      toast.error(contactEmailIssue);
      throw new Error(contactEmailIssue);
    }
    if (!dirty) return;
    const updates = collectPendingUpdates({
      unsavedUpdates: unsaved,
      hasCurrentProject: true,
    });
    try {
      await updateMutation.mutateAsync({ projectId: project.id, updates });
    } catch (error) {
      // E02-F03-T10 — surface the api's reason (e.g. the 400 VALIDATION_ERROR
      // message) instead of a bare generic line; identical repeats are collapsed
      // into one toast by ToastProvider.
      const reason = errorMessage(error, '');
      toast.error(reason ? `Could not save the project: ${reason}` : 'Could not save the project');
      throw error;
    }
    // PK-QA-11 — reset the ONE-SHOT client write-intent now that it has been
    // consumed. `form.clientId` (undefined = untouched, null = create-new,
    // number = linked id) is set ONLY by `onClientResolve` and was already
    // baked into the PATCH payload above (`collectPendingUpdates` ran before
    // `mutate`). If left set it stays armed for the rest of the mount, so
    // `diffForm` keeps staging `client_id` — which leaks the (already-saved)
    // id into EVERY subsequent PATCH the user fires from this header. Resetting
    // it makes the hidden intent self-heal like every other field: afterwards
    // `form.clientId === undefined === base.clientId`. Point-reset ONLY
    // `clientId`, NOT setForm(initialForm(project)): the invalidation refetch
    // has not landed yet, so `project` still holds the OLD role/client text and
    // a full re-seed would flash stale values.
    //
    // NB: this reset is NOT what clears the "unsaved changes" banner after a
    // Save — `hasUnsavedChanges` (projectUnsavedModel) only inspects
    // client_name / role_name / contact / manual, never `client_id`, so the
    // dirty flag and the resync gate never depended on this write-intent. The
    // banner clears when the refetch advances `base` to the persisted values
    // (the PK-QA-10 resync path). See decision-log PK-QA-11 for the corrected
    // root-cause note.
    setForm((f) => ({ ...f, clientId: undefined }));
    // E12-F17-T15 — a Saved project is no longer an abandonable draft.
    forgetFreshDraft(project.id);
    toast.success('Project saved');
    // PK-QA-1 UX — the user has saved their edits; drop any lingering
    // missing-field highlight so a stale outline never survives the save.
    onClearMissingFields?.();
  }

  /** Save button: fire-and-forget (the failure toast was already raised). */
  function onSave(): void {
    void saveStagedEdits().catch(() => undefined);
  }

  /**
   * E02-F03-T08 — publish this header's staged edits to the navigation guard, so
   * leaving the card (sidebar, any in-app navigate, tab close) while the "Unsaved
   * changes" marker is up opens the Save/Discard/Cancel dialog instead of silently
   * dropping the edits. The draft is projected onto ONE key deliberately: the
   * guard must fire in exactly the cases the visible dirty marker is shown, so it
   * reuses `dirty` (projectUnsavedModel) rather than re-deriving its own predicate
   * from the bag (which also counts `client_id`, a hidden write-intent).
   * `useRegisterUnsavedSource` reads the latest closures on every render, so these
   * inline handlers never go stale.
   */
  useRegisterUnsavedSource({
    getDraft: () => ({ unsavedUpdates: dirty ? { _dirty: true } : {} }),
    onSave: () => saveStagedEdits(),
    onDiscard: () => onCancel(),
  });

  const clients = clientsQuery.data ?? [];

  // E02-F07-T08 — grow the role textarea to fit its wrapped content (also after a
  // resync/Cancel replaces the value, and when the column width changes).
  const roleRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = roleRef.current;
    if (!el) return undefined;
    const fit = (): void => {
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    let lastWidth = el.clientWidth;
    const ro = new ResizeObserver(() => {
      if (el.clientWidth !== lastWidth) {
        lastWidth = el.clientWidth;
        fit();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [form.roleName]);

  return (
    <header
      data-testid="project-detail-header"
      className="rounded-lg bg-slate-900 p-6 text-slate-200 shadow-sm"
    >
      {/* FP-09 — v1 navy hero banner (project.js renderProjectHeader ~L625-674):
          Client (gold hero) + Role | Contact (read-only + pencil) | Status +
          Recruiter, split by faint dividers. */}
      <div className="grid gap-6 md:grid-cols-3 md:divide-x md:divide-white/10">
        {/* Col 1 — Client (HERO, gold) + Role (secondary). */}
        <div className="min-w-0 md:pr-6" data-testid="project-detail-client">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
            Client
          </span>
          <div className="mt-1 flex items-center gap-2">
            <ClientAutocomplete
              value={form.clientName}
              clients={clients}
              onResolve={onClientResolve}
              disabled={saving}
              testId="project-detail-client-autocomplete"
              inputClassName={`brand-font w-full border-b border-transparent bg-transparent text-2xl font-bold text-[#D4AF37] outline-none hover:border-white/20 focus:border-[#D4AF37] disabled:text-slate-500 placeholder:text-slate-600${
                highlightClient ? ` ${MISSING_FIELD_HIGHLIGHT_CLASS}` : ''
              }`}
            />
            <i
              className="fa-solid fa-pencil shrink-0 text-xs text-slate-500"
              aria-hidden
              title="Edit client"
            />
          </div>

          <div className="mt-3">
            <label
              htmlFor="project-role-input"
              className="text-xs font-semibold uppercase tracking-wide text-slate-400"
            >
              Role
            </label>
            {/* E02-F07-T08 — a single-line <input> clipped long roles ("CRS Roles
                (B1 Line Maintenance Engineer, B2 Avionics…") at the pencil. A
                one-row auto-growing textarea wraps the full text instead; Enter
                and pasted newlines are suppressed so the value stays one line. */}
            <div className="flex items-start gap-2">
              <textarea
                ref={roleRef}
                id="project-role-input"
                rows={1}
                data-testid="project-detail-role-input"
                value={form.roleName}
                disabled={saving}
                onChange={(e) => setForm((f) => ({ ...f, roleName: toSingleLine(e.target.value) }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') e.preventDefault();
                }}
                placeholder="New Position"
                className={`mt-1 block min-w-0 flex-1 resize-none overflow-hidden whitespace-pre-wrap break-words border-b border-transparent bg-transparent text-sm font-semibold leading-snug text-slate-100 outline-none hover:border-white/20 focus:border-[#D4AF37] disabled:text-slate-500 placeholder:text-slate-600${
                  highlightRole ? ` ${MISSING_FIELD_HIGHLIGHT_CLASS}` : ''
                }`}
              />
              <i
                className="fa-solid fa-pencil mt-2 shrink-0 text-xs text-slate-500"
                aria-hidden
                title="Edit role"
              />
            </div>
            {!roleValid ? (
              <p data-testid="project-detail-role-error" className="mt-1 text-xs text-red-400">
                {EMPTY_ROLE_NAME_ALERT}
              </p>
            ) : null}
          </div>
        </div>

        {/* Col 2 — Contact ({first_name, last_name, email}, T06): read-only text +
            a pencil that toggles the edit inputs (v1 parity). */}
        <div className="min-w-0 md:px-6" data-testid="project-detail-contact">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
              Contact
            </span>
            <button
              type="button"
              data-testid="project-detail-contact-edit-toggle"
              onClick={() => setEditingContact((e) => !e)}
              disabled={saving}
              title={editingContact ? 'Done editing contact' : 'Edit contact'}
              aria-label={editingContact ? 'Done editing contact' : 'Edit contact'}
              aria-pressed={editingContact}
              className="text-xs text-slate-400 hover:text-[#D4AF37] disabled:opacity-50"
            >
              <i
                className={editingContact ? 'fa-solid fa-check' : 'fa-solid fa-pencil'}
                aria-hidden
              />
            </button>
          </div>

          {editingContact ? (
            <>
              <div className="mt-2 flex gap-2">
                <input
                  type="text"
                  data-testid="project-detail-contact-first"
                  value={form.contactFirst}
                  disabled={saving}
                  onChange={(e) => setForm((f) => ({ ...f, contactFirst: e.target.value }))}
                  placeholder="First"
                  className="w-full rounded border border-white/15 bg-white/5 px-2 py-1.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-[#D4AF37] disabled:opacity-50"
                />
                <input
                  type="text"
                  data-testid="project-detail-contact-last"
                  value={form.contactLast}
                  disabled={saving}
                  onChange={(e) => setForm((f) => ({ ...f, contactLast: e.target.value }))}
                  placeholder="Last"
                  className="w-full rounded border border-white/15 bg-white/5 px-2 py-1.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-[#D4AF37] disabled:opacity-50"
                />
              </div>
              <div className="mt-2" data-testid="project-detail-contact-email">
                <input
                  type="email"
                  data-testid="project-detail-contact-email-input"
                  value={form.contactEmail}
                  disabled={saving}
                  onChange={(e) => setForm((f) => ({ ...f, contactEmail: e.target.value }))}
                  placeholder="name@company.com"
                  aria-invalid={contactEmailIssue ? true : undefined}
                  className={`w-full rounded border border-white/15 bg-white/5 px-2 py-1.5 text-sm text-slate-100 outline-none placeholder:text-slate-500 focus:border-[#D4AF37] disabled:opacity-50${
                    highlightContactEmail || contactEmailIssue
                      ? ` ${MISSING_FIELD_HIGHLIGHT_CLASS}`
                      : ''
                  }`}
                />
              </div>
            </>
          ) : (
            <div className="mt-1" data-testid="project-detail-contact-readonly">
              <p className="text-lg font-semibold text-slate-100">
                {contactName || <span className="text-slate-500">No contact</span>}
              </p>
              <p className="text-sm text-slate-400" data-testid="project-detail-contact-email">
                {form.contactEmail || '—'}
              </p>
            </div>
          )}
          {contactEmailIssue ? (
            <p
              data-testid="project-detail-contact-email-error"
              className="mt-1 text-xs text-red-400"
            >
              {contactEmailIssue}
            </p>
          ) : null}
        </div>

        {/* Col 3 — Status (UPPERCASE, legacy L672) + Recruiter/owner (legacy
            #detail-recruiter, L673). Right-aligned to mirror the legacy column. */}
        <div className="flex flex-col items-start md:items-end md:pl-6">
          <span
            data-testid="project-detail-status"
            className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${header.status.className}`}
          >
            {header.status.displayLabel}
          </span>
          <span
            data-testid="project-detail-recruiter"
            className="mt-2 text-xs text-[#D4AF37]"
            title="Recruiter"
          >
            {header.recruiter}
          </span>

          {/* E03-F02-T06 — "Analyzed"/"Not analyzed" pill: whether the
              Pre-Kickoff AI analysis (Stage-1) has ever completed for this
              project (project.last_analysis_at). */}
          <span
            data-testid="project-detail-analysis-status"
            className={`mt-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${header.analysisStatus.className}`}
          >
            {header.analysisStatus.label}
          </span>

          {/* E04-F02-T08 — reference back to the inbox email this project was
              created from. `source_email_id` is null for most projects (created
              manually), so this only renders for the email-originated ones. */}
          {project.source_email_id != null ? (
            <button
              type="button"
              data-testid="project-source-email-link"
              // E02-F03-T08 — this jump bypasses the shell's `navigate` (it pushes
              // history itself), so it must ask the guard on its own; otherwise a
              // dirty header could be left behind through this one button.
              onClick={() => guard(() => openInboxViaHistory(project.source_email_id!))}
              title="Open the source email in Inbox"
              className="mt-2 flex items-center gap-1 text-xs text-blue-300 hover:underline"
            >
              <i className="fa-solid fa-envelope" aria-hidden />
              Created from email
            </button>
          ) : null}
        </div>
      </div>

      {/* Save / Cancel — only relevant once something is staged (dirty). */}
      <div className="mt-5 flex items-center justify-end gap-2 border-t border-white/10 pt-4">
        {dirty ? (
          <span data-testid="project-detail-dirty" className="mr-auto text-xs text-amber-300">
            Unsaved changes
          </span>
        ) : null}
        <button
          type="button"
          data-testid="project-detail-cancel"
          onClick={onCancelClick}
          disabled={(!dirty && !onDiscardDraft) || saving}
          title={onDiscardDraft ? 'Discard this new project' : undefined}
          className="rounded border border-white/20 px-4 py-1.5 text-sm font-medium text-slate-200 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid="project-detail-save"
          onClick={onSave}
          disabled={!dirty || !roleValid || contactEmailIssue !== null || saving}
          className="rounded bg-[#D4AF37] px-4 py-1.5 text-sm font-semibold text-slate-900 hover:bg-[#B5952F] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? (
            <>
              <i className="fa-solid fa-spinner fa-spin mr-2" aria-hidden />
              Saving…
            </>
          ) : (
            'Save'
          )}
        </button>
      </div>
    </header>
  );
}

export default ProjectDetailHeader;
